import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { FaSearch, FaEdit, FaTrash, FaCheck, FaTimes, FaSpinner } from "react-icons/fa";
import { StandardURL } from "../App";


const PropertyTrashBillPage = () => {
  const [propertyId, setPropertyId] = useState("");
  const [trashBills, setTrashBills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [billToUpdate, setBillToUpdate] = useState(null);
  const [billToDelete, setBillToDelete] = useState(null);
  const [amount, setAmount] = useState("");

  const fetchTrashBills = async () => {
    if (!propertyId) {
      toast.error("Please enter a Property ID.");
      return;
    }
    setIsLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/admin/trashbill/getall/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setTrashBills(response.data.response || []);
      toast.success("Trash bills fetched successfully.");
    } catch (error) {
      console.error("Error fetching trash bills:", error);
      toast.error(error.response?.data?.response || "Error fetching trash bills.");
      setTrashBills([]);
    } finally {
      setIsLoading(false);
    }
  };

  const openUpdateModal = (bill) => {
    setBillToUpdate(bill);
    setAmount(bill.amount);
    setIsUpdateModalOpen(true);
  };

  const openDeleteModal = (bill) => {
    setBillToDelete(bill);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteTrashBill = async () => {
    setIsActionLoading(true);
    const token = localStorage.getItem("token");
    try {
      await axios.delete(
        `${StandardURL}/iNyumba/admin/trashbill/deletetrashbill/${billToDelete.trashbillid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Trash bill deleted successfully.");
      fetchTrashBills();
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting trash bill:", error);
      toast.error(error.response?.data?.response || "Failed to delete trash bill.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleUpdateTrashBill = async () => {
    if (!amount) {
      toast.error("Please enter a valid amount.");
      return;
    }
    setIsActionLoading(true);
    const token = localStorage.getItem("token");
    const data = { amount: Number(amount) };

    try {
      await axios.patch(
        `${StandardURL}/iNyumba/admin/trashbill/updatetrashbillid/${billToUpdate.trashbillid}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Trash bill updated successfully.");
      fetchTrashBills();
      setIsUpdateModalOpen(false);
    } catch (error) {
      console.error("Error updating trash bill:", error);
      toast.error(error.response?.data?.response || "Failed to update trash bill.");
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="container mx-auto px-4 py-8 bg-gray-100 min-h-screen">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Property Trash Bills</h1>
          <div className="flex items-center space-x-2 mb-6">
            <input
              type="text"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              placeholder="Enter Property ID"
              className="flex-grow border border-gray-300 rounded-md py-2 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={fetchTrashBills}
              disabled={isLoading}
              className={`bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md flex items-center transition-colors duration-200
                ${isLoading ? "bg-gray-400 cursor-not-allowed" : ""}`}
            >
              {isLoading ? (
                <Oval height={20} width={20} color="white" ariaLabel="loading" />
              ) : (
                <><FaSearch className="mr-2" /> Search</>
              )}
            </button>
          </div>

          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <Oval color="#4F46E5" height={50} width={50} />
              </div>
            ) : trashBills.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {propertyId ? "No trash bills found for this property ID." : "Enter a Property ID to search for trash bills."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rental ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">House Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount (Ksh)</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {trashBills.map((bill) => (
                      <tr key={bill.trashbillid}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{bill.trashbillid}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bill.rentalid}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bill.housenumber}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bill.userid}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bill.amount}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => openUpdateModal(bill)}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md flex items-center transition-colors duration-200"
                          >
                            <FaEdit className="mr-1" /> Update
                          </button>
                          <button
                            onClick={() => openDeleteModal(bill)}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md flex items-center transition-colors duration-200"
                          >
                            <FaTrash className="mr-1" /> Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Update Modal */}
        {isUpdateModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center">
            <div className="relative bg-white p-8 rounded-lg shadow-xl max-w-sm w-full">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">Update Trash Bill</h2>
              <div className="space-y-4">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter New Amount"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end mt-6 space-x-2">
                <button
                  onClick={handleUpdateTrashBill}
                  disabled={isActionLoading || !amount}
                  className={`flex items-center justify-center px-6 py-3 rounded-md font-semibold text-white transition-colors duration-200
                    ${isActionLoading || !amount ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  {isActionLoading ? <FaSpinner className="mr-2 animate-spin" /> : <FaCheck className="mr-2" />}
                  {isActionLoading ? "Updating..." : "Update Bill"}
                </button>
                <button
                  onClick={() => setIsUpdateModalOpen(false)}
                  className="px-6 py-3 rounded-md font-semibold bg-gray-500 hover:bg-gray-600 text-white transition-colors duration-200"
                >
                  <FaTimes className="mr-2" /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center">
            <div className="relative bg-white p-8 rounded-lg shadow-xl max-w-sm w-full text-center">
              <h3 className="text-xl font-bold mb-4">Confirm Deletion</h3>
              <p className="mb-6">Are you sure you want to delete this trash bill? This action cannot be undone.</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleDeleteTrashBill}
                  disabled={isActionLoading}
                  className={`flex items-center justify-center px-6 py-3 rounded-md font-semibold text-white transition-colors duration-200
                    ${isActionLoading ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
                >
                  {isActionLoading ? (
                    <FaSpinner className="mr-2 animate-spin" />
                  ) : (
                    <FaTrash className="mr-2" />
                  )}
                  {isActionLoading ? "Deleting..." : "Delete"}
                </button>
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-6 py-3 rounded-md font-semibold bg-gray-500 hover:bg-gray-600 text-white transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </AuthGuard>
    </EmailGuard>
  );
};

export default PropertyTrashBillPage;